/**
 * Erros tipados do client do Orquestra. Isolados de `OrquestraApi.ts` para
 * que helpers de transferência (`attachmentUpload.ts`) os usem sem ciclo de
 * import; `OrquestraApi.ts` os reexporta para os consumidores existentes.
 */
import type { GapDeleteImpactSolution } from '@internal/orquestra-contract';

/** Erro de requisição ao backend do Orquestra, carregando o status HTTP. */
export class OrquestraRequestError extends Error {
  /**
   * Mensagem enviada pelo backend (`{ error }`), quando houver — equivale ao
   * `detail` que a origem mostra no toast (`getErrorMessage`).
   */
  serverMessage?: string;

  constructor(
    message: string,
    public readonly status: number,
    /** `code` estruturado do backend (ex.: `PHASE_HAS_FINANCIAL_PARCELAS`). */
    public readonly code?: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'OrquestraRequestError';
  }
}

interface ErrorBody {
  error?: string;
  code?: string;
  details?: Record<string, unknown>;
}

/** Lê o corpo de erro (JSON opcional) e monta o `OrquestraRequestError`. */
export async function toRequestError(
  response: Response,
  path: string,
): Promise<OrquestraRequestError> {
  let body: ErrorBody = {};
  try {
    body = ((await response.json()) as ErrorBody) ?? {};
  } catch {
    // corpo sem JSON — mantém detalhe vazio
  }
  const error = new OrquestraRequestError(
    body.error ||
      `Requisição ao Orquestra falhou: ${path} (${response.status})`,
    response.status,
    body.code,
    body.details,
  );
  if (body.error) error.serverMessage = body.error;
  return error;
}

/**
 * Erro específico de upload de anexo quando o backend responde 413
 * (`Payload Too Large`), acima do limite `ATTACHMENT_MAX_BYTES` (RF4.3).
 */
export class AttachmentTooLargeError extends OrquestraRequestError {
  constructor(message = 'Arquivo excede o tamanho máximo permitido.') {
    super(message, 413);
    this.name = 'AttachmentTooLargeError';
  }
}

/**
 * Erro específico de upload de anexo quando o backend responde 400
 * (validação: extensão não permitida, projeto inexistente, etc.).
 */
export class AttachmentValidationError extends OrquestraRequestError {
  constructor(message = 'Anexo inválido.') {
    super(message, 400);
    this.name = 'AttachmentValidationError';
  }
}

/**
 * Erro específico da exclusão de GAP quando o backend responde 409
 * (`GAP_HAS_ORPHAN_SOLUTIONS`, RF3.3): há Soluções que ficariam órfãs
 * caso o GAP seja removido. Carrega a lista de soluções órfãs para o
 * modal exibir de volta ao usuário.
 */
export class GapDeleteConflictError extends OrquestraRequestError {
  constructor(
    message: string,
    public readonly orphanedSolutions: GapDeleteImpactSolution[],
  ) {
    super(message, 409);
    this.name = 'GapDeleteConflictError';
  }
}
